import * as React from "react";
import { saveLocale, setLocaleEnabled, type AdminLocale } from "@/api/admin";
import { useAction } from "@/lib/useAsync";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/input";
import { ConfirmModal, Modal } from "@/components/ui/modal";
import { Notice } from "@/components/ui/feedback";
import { SkeletonRows } from "@/components/ui/skeleton";
import { TBody, TD, TEmpty, TH, THead, TR, TableShell } from "@/components/ui/table";

/**
 * The languages this shop speaks, and the switch that publishes one.
 *
 * SEPARATE FROM THE STRINGS EDITOR, in its own file for the reason the worker
 * keeps them on two routes: turning a language ON is what makes it reachable to
 * visitors and to search engines, and editing its words is not. One of the two
 * can take every page of the site away from every visitor, and it is not the
 * one with the name field on it. They shared a 585-line file, which is the
 * arrangement that invites a helper written for one to be reached for by the
 * other.
 */

export function LanguagesCard({
  locales,
  loading,
  onChanged,
}: {
  locales: AdminLocale[];
  loading: boolean;
  onChanged: () => void;
}) {
  const action = useAction();
  const [editing, setEditing] = React.useState<AdminLocale | null>(null);
  const [disabling, setDisabling] = React.useState<AdminLocale | null>(null);

  const enabledCount = locales.filter((l) => l.enabled).length;

  async function act(fn: () => Promise<unknown>) {
    const ok = await action.run(fn);
    onChanged();
    return ok;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Languages</CardTitle>
        <span className="text-xs text-muted-foreground">
          A language is off until every string it needs exists.
        </span>
      </CardHeader>
      {action.error && (
        <div className="px-4 pt-3">
          <Notice tone="error">{action.error}</Notice>
        </div>
      )}
      {loading && locales.length === 0 ? (
        <SkeletonRows rows={2} cols={5} />
      ) : (
        <TableShell>
          <THead>
            <TH>Code</TH>
            <TH>Name</TH>
            <TH numeric>Site strings</TH>
            <TH numeric>Email strings</TH>
            <TH>State</TH>
            <TH />
          </THead>
          <TBody>
            {locales.length === 0 && <TEmpty colSpan={6}>No languages yet.</TEmpty>}
            {locales.map((locale) => {
              // The worker refuses this too, and it is the one that decides.
              // Disabling the control is about not offering an action that
              // cannot work.
              const onlyOne = locale.enabled && enabledCount <= 1;
              return (
                <TR key={locale.code}>
                  <TD className="font-mono text-xs">{locale.code}</TD>
                  <TD>
                    {locale.flag && <span className="mr-2">{locale.flag}</span>}
                    <span className="font-medium">{locale.name}</span>
                  </TD>
                  <TD numeric>{locale.counts.site}</TD>
                  <TD numeric>{locale.counts.email}</TD>
                  <TD>
                    {locale.enabled ? (
                      <Badge color="green">visitors see it</Badge>
                    ) : (
                      <Badge color="gray">hidden</Badge>
                    )}
                  </TD>
                  <TD>
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(locale)}>
                        Rename
                      </Button>
                      {locale.enabled ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={action.busy || onlyOne}
                          title={onlyOne ? "The last language cannot be turned off" : undefined}
                          onClick={() => setDisabling(locale)}
                        >
                          Turn off
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={action.busy}
                          onClick={() => void act(() => setLocaleEnabled(locale.code, true))}
                        >
                          Turn on
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </TableShell>
      )}

      <RenameModal
        locale={editing}
        busy={action.busy}
        onClose={() => setEditing(null)}
        onSave={async (body) => {
          if (editing && (await act(() => saveLocale(editing.code, body)))) setEditing(null);
        }}
      />

      <ConfirmModal
        open={disabling !== null}
        title="Turn this language off?"
        confirmLabel="Turn it off"
        busy={action.busy}
        onClose={() => setDisabling(null)}
        body={
          <>
            <p>
              Every page in <span className="font-medium">{disabling?.name}</span> stops being built
              at the next publish, and its URLs stop existing.
            </p>
            <p className="mt-2">The strings are kept. Turning it back on restores the pages.</p>
          </>
        }
        onConfirm={() => {
          if (!disabling) return;
          void act(() => setLocaleEnabled(disabling.code, false)).then(() => setDisabling(null));
        }}
      />
    </Card>
  );
}

function RenameModal({
  locale,
  busy,
  onClose,
  onSave,
}: {
  locale: AdminLocale | null;
  busy: boolean;
  onClose: () => void;
  onSave: (body: { name: string; flag: string | null; sort: number }) => void;
}) {
  const [name, setName] = React.useState("");
  const [flag, setFlag] = React.useState("");
  const [sort, setSort] = React.useState("0");

  React.useEffect(() => {
    if (!locale) return;
    setName(locale.name);
    setFlag(locale.flag ?? "");
    setSort(String(locale.sort));
  }, [locale]);

  if (!locale) return null;

  return (
    <Modal
      open
      title={`Language ${locale.code}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            disabled={busy || name.trim().length === 0}
            onClick={() => onSave({ name: name.trim(), flag: flag.trim() || null, sort: Number(sort) || 0 })}
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Whether it is ON is not on this form, and that is deliberate: the
            worker will not let this route reach `enabled` at all. */}
        <Field label="Name" htmlFor="loc-name" hint="As the language calls itself.">
          <Input id="loc-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Flag" htmlFor="loc-flag" hint="Optional. One emoji, shown in the switcher.">
          <Input id="loc-flag" value={flag} onChange={(e) => setFlag(e.target.value)} />
        </Field>
        <Field label="Order" htmlFor="loc-sort" hint="Lower comes first in the switcher.">
          <Input id="loc-sort" type="number" value={sort} onChange={(e) => setSort(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
